import {
  MinusCircleOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

// Get file type and icon based on file extension
export const getFileType = (type, url, displayName) => {
  // Handle website type
  if (type === 'WEBSITE_URL') {
    return { icon: 'default' };
  }

  // Get file name from URL or display name
  const fileName = url?.split('/').pop() || displayName || '';

  // Extract extension (supports multiple dots in filename)
  const extension = fileName.includes('.')
    ? fileName.split('.').pop().toLowerCase()
    : '';

  // Define supported file types and their corresponding icons
  const fileTypeMap = {
    // Document formats
    pdf: 'pdf',
    doc: 'doc',
    docx: 'doc',
    txt: 'txt',
    rtf: 'default',
    odt: 'default',

    // Spreadsheet formats
    xls: 'default',
    xlsx: 'default',
    csv: 'default',
    ods: 'default',

    // Presentation formats
    ppt: 'ppt',
    pptx: 'ppt',
    odp: 'ppt',

    // Markup/Code
    md: 'md',
    json: 'json',
    xml: 'default',
    html: 'default',
    htm: 'default',

    // Image formats
    jpg: 'default',
    jpeg: 'default',
    png: 'default',
    gif: 'default',
    svg: 'default',
    webp: 'default',

    // Archive formats
    zip: 'default',
    rar: 'default',
    '7z': 'default',
    tar: 'default',
    gz: 'default',
  };

  // Return the mapped file type or 'default' if not found
  return {
    icon: fileTypeMap[extension] || 'default',
    type: `${fileTypeMap[extension] || 'default'} File`,
  };
};

// Get status information based on status key
export const getStatus = (status) => {
  const statusMap = {
    draft: { label: 'New', color: 'default', icon: <MinusCircleOutlined /> },
    ds_enqueued: {
      label: 'Learning',
      color: 'processing',
      icon: <SyncOutlined spin />,
    },
    processing: {
      label: 'Learning',
      color: 'processing',
      icon: <SyncOutlined spin />,
    },
    processed: {
      label: 'Learning',
      color: 'processing',
      icon: <SyncOutlined spin />,
    },
    corpus_enqueued: {
      label: 'Learning',
      color: 'processing',
      icon: <SyncOutlined spin />,
    },
    uploading: {
      label: 'Learning',
      color: 'processing',
      icon: <SyncOutlined spin />,
    },
    uploaded: {
      label: 'Ready',
      color: 'success',
      icon: <CheckCircleOutlined />,
    },
    deleted: { label: 'Deleted', color: 'error', icon: <DeleteOutlined /> },
    process_error: {
      label: 'Error',
      color: 'error',
      icon: <CloseCircleOutlined />,
    },
    upload_error: {
      label: 'Error',
      color: 'error',
      icon: <CloseCircleOutlined />,
    },
    no_files_imported: {
      label: 'Error',
      color: 'error',
      icon: <CloseCircleOutlined />,
    },
  };
  return statusMap[status] || { label: 'Unknown', color: 'default' };
};

// Clean and format a file name
// @param {string} name - The original file name (with or without extension)
// @param {string} [defaultPrefix='file'] - Default prefix to use if name is empty
// @returns {string} Cleaned and formatted file name without extension
export const cleanFileName = (name, defaultPrefix = 'file') => {
  return (
    (name || '')
      .trim()
      // Remove any existing extension
      .replace(/\.[^/.]+$/, '')
      // Replace spaces and special characters with underscores
      .replaceAll(/[^\w-]/g, '_')
      // Remove consecutive underscores
      .replaceAll(/_{2,}/g, '_')
      // Remove leading/trailing underscores
      .replaceAll(/(^_+|_+$)/g, '') ||
    // Ensure it's not empty
    `${defaultPrefix}_${Date.now()}`
  );
};
